import { createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../AppConfig";
import { LeftPanelTabs, RightPanelTabs } from "../constants/Panels";
import { Tool } from "../utils/arcgisUtils";

interface NauticalImageContext {
    open: boolean;
    sjomerkeNr?: string;
    imageUrl?: string;
}

interface AppState {
    darkmode: boolean;
    measureToolOpen: boolean;
    esriLayerListOpen: boolean;
    layerListOpen: boolean;
    nauticalImageContext: NauticalImageContext;
    createObjectOpen: boolean;
    viewLoaded: boolean;
    featureResultsOpen: boolean;
    featureEditorOpen: boolean;
    versionControlOpen: boolean;
    overettOpen: boolean;
    lightCalculatorOpen: { open: boolean; loadFeatureValues: boolean };
    mapScale: number;
    loading: boolean;
    leftPanelOpen: boolean;
    leftPanelActiveTab: LeftPanelTabs;
    rightPanelOpen: boolean;
    rightPanelActiveTab: RightPanelTabs;
    boatLocationWindowOpen: boolean;
    boatStatusOpen: boolean;
    jobsPanelOpen?: boolean;
    defaultMinScales: { [layerName: string]: number };
    isSynchronizingToFDV?: boolean;
    showRemovedFeatures: boolean;
    isOpeningTool: Tool | null;
    hasUnsavedChanges: boolean;
}

const initialState: AppState = {
    darkmode: false,
    measureToolOpen: false,
    esriLayerListOpen: false,
    layerListOpen: false,
    nauticalImageContext: { open: false },
    createObjectOpen: false,
    viewLoaded: false,
    featureResultsOpen: false,
    featureEditorOpen: false,
    versionControlOpen: false,
    overettOpen: false,
    lightCalculatorOpen: { open: false, loadFeatureValues: false },
    mapScale: AppConfig.Scale.DefaultScale,
    loading: false,
    leftPanelOpen: false,
    leftPanelActiveTab: LeftPanelTabs.UNDEFINED,
    rightPanelOpen: false,
    rightPanelActiveTab: RightPanelTabs.UNDEFINED,
    boatLocationWindowOpen: false,
    boatStatusOpen: false,
    jobsPanelOpen: false,
    defaultMinScales: {},
    isSynchronizingToFDV: false,
    showRemovedFeatures: false,
    isOpeningTool: null,
    hasUnsavedChanges: false,
};

const appSlice = createSlice({
    name: "app",
    initialState,
    reducers: {
        toggleDarkmode(state) {
            state.darkmode = !state.darkmode;
        },
        setDarkmode(state, action) {
            state.darkmode = action.payload;
        },
        setMeasureToolOpen(state, action) {
            state.measureToolOpen = action.payload;
        },
        setEsriLayerListOpen(state, action) {
            state.esriLayerListOpen = action.payload;
        },
        setLayerListOpen(state, action) {
            state.layerListOpen = action.payload;
        },
        setNauticalImageContext(state, action) {
            state.nauticalImageContext = action.payload;
        },
        setCreateObjectOpen(state, action) {
            state.createObjectOpen = action.payload;
        },
        setViewLoaded(state) {
            state.viewLoaded = true;
        },
        setFeatureResultsOpen(state, action) {
            state.featureResultsOpen = action.payload;
        },
        setFeatureEditorOpen(state, action) {
            state.featureEditorOpen = action.payload;
        },
        setVersionControlOpen(state, action) {
            state.versionControlOpen = action.payload;
        },
        setLightCalculatorOpen(state, action) {
            state.lightCalculatorOpen = action.payload;
        },
        setOverettOpen(state, action) {
            state.overettOpen = action.payload;
        },
        setMapScale(state, action) {
            state.mapScale = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setLeftPanelOpen(state, action) {
            state.leftPanelOpen = action.payload;
        },
        setLeftPanelActiveTab(state, action: { type: string; payload: LeftPanelTabs }) {
            state.leftPanelActiveTab = action.payload;
        },
        setRightPanelOpen(state, action) {
            state.rightPanelOpen = action.payload;
        },
        setRightPanelActiveTab(state, action: { type: string; payload: RightPanelTabs }) {
            state.rightPanelActiveTab = action.payload;
        },
        setBoatLocationWindowOpen(state, action) {
            state.boatLocationWindowOpen = action.payload;
        },
        setBoatStatusOpen(state, action) {
            state.boatStatusOpen = action.payload;
        },
        setJobsPanelOpen(state, action) {
            state.jobsPanelOpen = action.payload;
        },
        addDefaultMinScale(state, action) {
            const { layerName, minScale } = action.payload;
            state.defaultMinScales[layerName] = minScale;
        },
        setIsSynchronizingToFDV(state, action) {
            state.isSynchronizingToFDV = action.payload;
        },
        setShowRemovedFeatures(state, action) {
            state.showRemovedFeatures = action.payload;
        },
        setIsOpeningTool(state, action) {
            state.isOpeningTool = action.payload;
        },
        setHasUnsavedChanges(state, action) {
            state.hasUnsavedChanges = action.payload;
        },
    },
});

export default appSlice.reducer;

// Thunk action creator for changing right panel tab with validation
export const changeRightPanelTab = (newTab: RightPanelTabs) => {
    return (dispatch: any, getState: any): boolean => {
        const state = getState();
        if (state.app.hasUnsavedChanges && state.app.rightPanelActiveTab !== RightPanelTabs.UNDEFINED) {
            if (
                !globalThis.confirm(
                    "Du er i ferd med å forlate verktøyet med endringer som ikke er lagret. Er du sikker på at du vil fortsette? Dette medfører at endringene går tapt."
                )
            ) {
                return false;
            }
        }
        dispatch(setHasUnsavedChanges(false));
        dispatch(setRightPanelActiveTab(newTab));
        return true;
    };
};

export const {
    toggleDarkmode,
    setDarkmode,
    setViewLoaded,
    setMeasureToolOpen,
    setLayerListOpen,
    setNauticalImageContext,
    setCreateObjectOpen,
    setFeatureResultsOpen,
    setFeatureEditorOpen,
    setVersionControlOpen,
    setLightCalculatorOpen,
    setOverettOpen,
    setMapScale,
    setLoading,
    setLeftPanelOpen,
    setLeftPanelActiveTab,
    setRightPanelOpen,
    setRightPanelActiveTab,
    setBoatLocationWindowOpen,
    setBoatStatusOpen,
    setJobsPanelOpen,
    addDefaultMinScale,
    setIsSynchronizingToFDV,
    setShowRemovedFeatures,
    setIsOpeningTool,
    setHasUnsavedChanges,
} = appSlice.actions;
export type { AppState };
